import { useCallback } from 'react'

import {
  type InfiniteData,
  type Query,
  useInfiniteQuery,
} from '@tanstack/react-query'

import { useAuthInfo, useOrgHelper } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import {
  buildPaginatedEndpoint,
  convertDatesInObject,
  getNextPageParam,
} from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { organizationsQueryKeys } from '@/hooks/organization/types'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { Organization } from '@/types/Organization'

interface UseOrganizationsArgs<T> {
  select?: (
    data: InfiniteData<PaginatedResponse<Organization>, PaginateRequest>,
  ) => T
  allOrgs?: boolean
  enabled?: boolean
}

const fetchOrganizations = async (
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
  allOrgs?: boolean,
): Promise<PaginatedResponse<Organization>> => {
  if (accessToken === null || accessToken === undefined) {
    return { count: 0, has_more: false, data: [] }
  }
  const baseUrl = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/`,
    pagination,
  )
  const url = new URL(baseUrl)

  if (allOrgs) {
    url.searchParams.set('all_orgs', 'true')
  }

  const res: Response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
  return convertDatesInObject(await res.json())
}

export const useOrganizations = <T = Organization[]>(
  args?: UseOrganizationsArgs<T>,
) => {
  const { accessToken } = useAuthInfo()
  const { orgHelper } = useOrgHelper()

  const select = useCallback(
    (data: InfiniteData<PaginatedResponse<Organization>, PaginateRequest>) => {
      if (args?.select) {
        return args.select(data)
      }
      return data.pages.map((res) => res.data).flat() as unknown as T
    },
    [args?.select],
  )

  const refetchInterval = useCallback(
    (
      query: Query<
        PaginatedResponse<Organization>,
        Error,
        InfiniteData<PaginatedResponse<Organization>, PaginateRequest>
      >,
    ) => {
      if (query.state.data !== undefined) {
        // if the user has no orgs, but they have onboarded (indicated by orgHelper.getOrgIds().length > 0),
        // then the propelauth webhook hasn't fired yet, and we should continue to refetch
        const orgs = query.state.data.pages[0]?.data || []
        if (
          orgs.length === 0 &&
          orgHelper &&
          orgHelper.getOrgIds().length > 0
        ) {
          return 2000
        }
        return false
      }
      return 2000
    },
    [orgHelper?.getOrgIds],
  )

  return useInfiniteQuery({
    queryFn: ({ pageParam }) =>
      fetchOrganizations(accessToken, pageParam, args?.allOrgs),
    queryKey: args?.allOrgs
      ? organizationsQueryKeys.admin()
      : organizationsQueryKeys.all(),
    initialPageParam: { limit: 10000 }, // TODO
    getNextPageParam: getNextPageParam,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled:
      (args?.enabled ?? true) &&
      accessToken !== null &&
      accessToken !== undefined,
    select,
    refetchInterval,
  })
}
